// src/pages/redress/RedressDashboard.tsx
//
// Firm-wide redress scheme dashboard (Milestone 6). Mounted under
// ``/admin/redress-dashboard`` for admin users. Renders:
//
//   * Status buckets -- one card per RedressStatus with count + percentage.
//   * Scheme split -- tiny bar of scheme_1 / scheme_2 / out_of_scope.
//   * Financial totals panel -- expected / received / paid / fees / VAT /
//     late interest / pending.
//   * Ageing panel -- three urgent counters (PRDs lapsing in 7 days,
//     payments overdue, FOS deadlines within 30 days).
//   * Refresh button (no auto-polling -- project memory:
//     ``feedback_cosmos_multi_instance``). Every click re-queries Cosmos.
//
// Dark-launched behind REACT_APP_REDRESS_ENABLED so the link / route can
// hide in production until the backend flag is flipped.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BulkUploadCommitResult,
  BulkUploadPreview,
  FirmRedressSummary,
  redressApi,
} from '../../api/redress';
import {
  applyDisabled,
  bannerDangerStyle,
  buttonNeutral,
  buttonPrimary,
  buttonSecondary,
  buttonSuccess,
  colors,
  humaniseScheme,
  humaniseStatus,
  pillForStatus,
  tdStyle,
} from './styles';

const REDRESS_ENABLED: boolean =
  (process.env.REACT_APP_REDRESS_ENABLED ?? 'true').toLowerCase() === 'true';

function formatMoney(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '-';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCount(n: number): string {
  return n.toLocaleString();
}

function percentage(part: number, whole: number): string {
  if (!whole) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}

function extractError(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { error?: string; reason?: string } };
    message?: string;
  };
  return (
    ax.response?.data?.error ||
    ax.response?.data?.reason ||
    ax.message ||
    fallback
  );
}

// Order statuses how ops want to scan them (invited-first, terminal-last).
// H4: MUST cover the backend's full RedressStatus enum
// (redress_models.REDRESS_STATUS_VALUES) — a status missing here is an
// invisible bucket on the dashboard.
const STATUS_ORDER: ReadonlyArray<string> = [
  // Live pipeline, PRD stage.
  'invited',
  'joined',
  'awaiting_prd',
  'held_high_value',
  'prd_received',
  'prd_sent_to_client',
  'client_objecting',
  'objection_submitted',
  // RD stage.
  'awaiting_rd',
  'rd_sent_to_client',
  // Acceptance / payment tail.
  'client_accepted',
  'fd_received',
  'paid',
  // Terminal: client exits.
  'opted_out',
  'rd_no_response_closed',
  'rejected_fos_referral',
  // Terminal: liability closures (six-gate + legacy).
  'closed_not_scheme_case',
  'closed_outside_subject_matter',
  'closed_exception_exists',
  'closed_no_arrangement',
  'closed_no_unfair_relationship',
  'closed_no_loss',
  'closed_no_commission',
];

const SCHEME_ORDER: ReadonlyArray<string> = ['scheme_1', 'scheme_2', 'out_of_scope'];

export default function RedressDashboard(): React.ReactElement {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<FirmRedressSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await redressApi.getFirmRedressSummary();
      setSummary(data);
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to load redress summary'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!REDRESS_ENABLED) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [refresh]);

  const totalCases = summary?.caseCount ?? 0;
  // P1-9: byStatus/byScheme count AGREEMENTS; use the matching
  // denominator for percentages (fall back to caseCount for an old
  // backend that predates agreementCount).
  const totalAgreements = summary?.agreementCount ?? totalCases;

  const statusCards = useMemo(() => {
    if (!summary) return [];
    const by = summary.byStatus;
    return STATUS_ORDER.map((status) => ({
      status,
      count: by[status] ?? 0,
    }));
  }, [summary]);

  const schemeCards = useMemo(() => {
    if (!summary) return [];
    const by = summary.byScheme;
    return SCHEME_ORDER.map((scheme) => ({
      scheme,
      count: by[scheme] ?? 0,
    }));
  }, [summary]);

  if (!REDRESS_ENABLED) {
    return (
      <div style={{ padding: 24 }}>
        <em style={{ color: colors.neutral500 }}>
          Redress scheme is disabled in this environment.
        </em>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <header style={headerStyle}>
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 700,
              color: colors.neutral900,
              letterSpacing: '-0.01em',
            }}
          >
            Firm-wide Redress Dashboard
          </h2>
          <small style={{ color: colors.neutral500, fontSize: 13 }}>
            {summary
              ? `Snapshot generated ${new Date(
                  summary.generatedAt,
                ).toLocaleString()}`
              : 'Loading live figures…'}
          </small>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          style={applyDisabled(buttonPrimary, loading)}
          aria-busy={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {error !== null && (
        <div style={bannerDangerStyle} role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* M7 -- Bulk upload panel. Rendered always (not gated on summary). */}
      <BulkUploadPanel onCommitted={() => void refresh()} />

      {summary === null && !loading && !error && (
        <div
          style={{
            padding: 32,
            textAlign: 'center',
            color: colors.neutral500,
            background: colors.neutral50,
            border: `1px dashed ${colors.neutral200}`,
            borderRadius: 12,
          }}
        >
          No data available. Try refreshing.
        </div>
      )}

      {loading && summary === null && (
        <div style={{ padding: 24, color: colors.neutral500 }}>
          Loading firm-wide totals…
        </div>
      )}

      {summary !== null && (
        <>
          {/* Ageing -- the most urgent cards, right up top. */}
          <section style={sectionStyle}>
            <h3 style={sectionHeading}>Ageing</h3>
            <div style={gridStyle}>
              <UrgentCard
                label="PRDs lapsing in 7 days"
                value={summary.ageing.prdLapsingIn7d}
                tone={summary.ageing.prdLapsingIn7d > 0 ? 'warn' : 'ok'}
              />
              <UrgentCard
                label="Payments overdue"
                value={summary.ageing.paymentsOverdue}
                tone={summary.ageing.paymentsOverdue > 0 ? 'danger' : 'ok'}
              />
              <UrgentCard
                label="FOS deadlines within 30 days"
                value={summary.ageing.fosDeadlineWithin30d}
                tone={summary.ageing.fosDeadlineWithin30d > 0 ? 'warn' : 'ok'}
                onClick={() => navigate('/admin/redress-fos-referrals')}
              />
            </div>
          </section>

          {/* Financial totals. */}
          <section style={sectionStyle}>
            <h3 style={sectionHeading}>Financial totals</h3>
            <div style={gridStyle}>
              <MoneyCard
                label="Expected receivable from lenders"
                value={summary.totals.expectedReceivableFromLenders}
              />
              <MoneyCard
                label="Received from lenders"
                value={summary.totals.totalReceivedFromLenders}
              />
              <MoneyCard
                label="Paid to clients"
                value={summary.totals.totalPaidToClients}
              />
              <MoneyCard
                label="CL fees"
                value={summary.totals.totalClFees}
              />
              <MoneyCard
                label="VAT collected"
                value={summary.totals.totalVatCollected}
              />
              <MoneyCard
                label="Late interest accrued"
                value={summary.totals.totalLateInterest}
              />
              <MoneyCard
                label="Pending payout"
                value={summary.totals.totalPendingPayout}
                emphasis
              />
            </div>
          </section>

          {/* Status buckets. */}
          <section style={sectionStyle}>
            <h3 style={sectionHeading}>
              Status{' '}
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: colors.neutral500,
                }}
              >
                ({formatCount(totalAgreements)} agreements across{' '}
                {formatCount(totalCases)} cases)
              </span>
            </h3>
            <div style={statusGridStyle}>
              {statusCards.map(({ status, count }) => (
                <div key={status} style={statusCardStyle}>
                  <span
                    style={{
                      ...pillForStatus(status),
                      fontSize: 11,
                      padding: '2px 8px',
                    }}
                  >
                    {humaniseStatus(status)}
                  </span>
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: 700,
                      marginTop: 8,
                      color: colors.neutral900,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatCount(count)}
                  </div>
                  <div style={{ fontSize: 12, color: colors.neutral500 }}>
                    {percentage(count, totalAgreements)} of total
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Scheme split. */}
          <section style={sectionStyle}>
            <h3 style={sectionHeading}>Scheme split</h3>
            <div style={schemeBarWrapStyle}>
              {schemeCards.map(({ scheme, count }) => (
                <SchemeBar
                  key={scheme}
                  scheme={scheme}
                  count={count}
                  total={totalAgreements}
                />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

// --- Bulk upload panel (Milestone 7) ----------------------------------------

function BulkUploadPanel(
  { onCommitted }: { onCommitted: () => void },
): React.ReactElement {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BulkUploadPreview | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [skipErrors, setSkipErrors] = useState<boolean>(false);
  const [commitResult, setCommitResult] =
    useState<BulkUploadCommitResult | null>(null);
  const [expandErrors, setExpandErrors] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resetAll = useCallback((): void => {
    setFile(null);
    setPreview(null);
    setError(null);
    setSkipErrors(false);
    setCommitResult(null);
    setExpandErrors(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleTemplate = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await redressApi.downloadBulkUploadTemplate();
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to download template'));
    } finally {
      setBusy(false);
    }
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const picked = e.target.files?.[0] ?? null;
      setFile(picked);
      setPreview(null);
      setCommitResult(null);
      setError(null);
      if (!picked) return;
      setBusy(true);
      try {
        const data = await redressApi.previewBulkUpload(picked);
        setPreview(data);
      } catch (err: unknown) {
        setError(extractError(err, 'Failed to preview upload'));
      } finally {
        setBusy(false);
      }
    }, []);

  const handleCommit = useCallback(async (): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const res = await redressApi.commitBulkUpload(file, skipErrors);
      setCommitResult(res);
      onCommitted();
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to commit upload'));
    } finally {
      setBusy(false);
    }
  }, [file, skipErrors, onCommitted]);

  const errorRows = useMemo(
    () => preview?.rows.filter((r) => r.errors.length > 0) ?? [],
    [preview],
  );

  const allValid = preview !== null && preview.errorRows === 0;
  const commitEnabled =
    preview !== null
    && !busy
    && (allValid || skipErrors)
    && preview.validRows > 0;

  return (
    <section
      style={{
        ...sectionStyle,
        border: `1px solid ${colors.primaryBorder}`,
        background: '#f4faff',
        padding: 20,
        borderRadius: 12,
      }}
    >
      <h3 style={{ ...sectionHeading, marginTop: 0 }}>Bulk upload</h3>
      <p style={{ color: colors.neutral600, marginTop: 0, fontSize: 14 }}>
        Upload a lender&apos;s PRD spreadsheet (.xlsx canonical template or
        .csv) to preview and then apply changes to multiple redress cases
        at once.
      </p>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          onClick={() => void handleTemplate()}
          disabled={busy}
          style={applyDisabled(buttonSecondary, busy)}
        >
          Download template
        </button>
        <input
          type="file"
          ref={fileInputRef}
          accept=".xlsx,.xlsm,.csv"
          onChange={(e) => void handleFileChange(e)}
          disabled={busy}
          style={{
            padding: 4,
            fontSize: 13,
          }}
          aria-label="Select bulk upload file"
        />
        {file !== null && (
          <button
            type="button"
            onClick={resetAll}
            disabled={busy}
            style={applyDisabled(buttonNeutral, busy)}
          >
            Clear
          </button>
        )}
      </div>

      {error !== null && (
        <div style={bannerDangerStyle} role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {preview !== null && (
        <div style={{ marginTop: 8 }}>
          <div
            style={{
              display: 'flex',
              gap: 16,
              marginBottom: 8,
              flexWrap: 'wrap',
              fontSize: 14,
            }}
          >
            <span>
              Total rows: <strong>{preview.totalRows}</strong>
            </span>
            <span style={{ color: colors.success }}>
              Valid: <strong>{preview.validRows}</strong>
            </span>
            <span
              style={{
                color: preview.errorRows > 0 ? colors.danger : colors.neutral500,
              }}
            >
              Errors: <strong>{preview.errorRows}</strong>
            </span>
          </div>

          {preview.summaryErrors.length > 0 && (
            <details style={{ marginBottom: 8 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>
                {preview.summaryErrors.length} file-level warning(s)/error(s)
              </summary>
              <ul>
                {preview.summaryErrors.map((m, i) => (
                  <li key={i} style={{ fontSize: 13, color: colors.neutral600 }}>
                    {m}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {errorRows.length > 0 && (
            <details
              open={expandErrors}
              onToggle={(e) =>
                setExpandErrors((e.target as HTMLDetailsElement).open)
              }
              style={{ marginBottom: 8 }}
            >
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>
                Show {errorRows.length} errored row(s)
              </summary>
              <table
                style={{
                  width: '100%',
                  fontSize: 13,
                  borderCollapse: 'collapse',
                  marginTop: 8,
                }}
              >
                <thead>
                  <tr style={{ background: colors.neutral100 }}>
                    <th style={tdStyle}>Row</th>
                    <th style={tdStyle}>Claim Ref</th>
                    <th style={tdStyle}>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {errorRows.map((r) => (
                    <tr key={`${r.row_number}-${r.claimReference}`}>
                      <td style={tdStyle}>{r.row_number}</td>
                      <td style={tdStyle}>{r.claimReference || '-'}</td>
                      <td style={tdStyle}>
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {r.errors.map((e, idx) => (
                            <li key={idx}>{e}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          {preview.errorRows > 0 && (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
                fontSize: 13,
                color: colors.warning,
              }}
            >
              <input
                type="checkbox"
                checked={skipErrors}
                onChange={(e) => setSkipErrors(e.target.checked)}
                disabled={busy}
              />
              Commit anyway, skipping errored rows
            </label>
          )}

          <div>
            <button
              type="button"
              onClick={() => void handleCommit()}
              disabled={!commitEnabled}
              style={applyDisabled(
                allValid ? buttonSuccess : { ...buttonSuccess, background: colors.warning, borderColor: colors.warning },
                !commitEnabled,
              )}
              aria-busy={busy}
            >
              {allValid ? 'Commit upload' : 'Commit valid rows'}
            </button>
          </div>
        </div>
      )}

      {commitResult !== null && (
        <div
          style={{
            marginTop: 12,
            background: colors.successLight,
            borderLeft: `3px solid ${colors.success}`,
            padding: 12,
            borderRadius: 6,
          }}
        >
          <strong style={{ color: colors.success }}>Upload committed.</strong>
          <ul style={{ marginTop: 4, fontSize: 14 }}>
            <li>Applied: {commitResult.applied}</li>
            <li>Skipped: {commitResult.skipped}</li>
            <li>Failures: {commitResult.failures.length}</li>
          </ul>
          {commitResult.failures.length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>
                Show failures
              </summary>
              <ul>
                {commitResult.failures.map((f, i) => (
                  <li key={i} style={{ fontSize: 13 }}>
                    {f.claimReference}: {f.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

// --- Cards ------------------------------------------------------------------

function MoneyCard({
  label, value, emphasis,
}: {
  label: string; value: number; emphasis?: boolean;
}): React.ReactElement {
  return (
    <div
      style={{
        ...cardStyle,
        background: emphasis ? colors.warningLight : colors.white,
        borderColor: emphasis ? colors.warningBorder : colors.neutral200,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: colors.neutral500,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: emphasis ? 28 : 24,
          fontWeight: 700,
          color: value < 0 ? colors.danger : colors.neutral900,
          fontVariantNumeric: 'tabular-nums',
          marginTop: 6,
          letterSpacing: '-0.01em',
        }}
      >
        £&nbsp;{formatMoney(value)}
      </div>
    </div>
  );
}

function UrgentCard({
  label, value, tone, onClick,
}: {
  label: string;
  value: number;
  tone: 'ok' | 'warn' | 'danger';
  onClick?: () => void;
}): React.ReactElement {
  const palette = (
    tone === 'danger'
      ? { bg: colors.dangerLight, border: colors.dangerBorder, fg: colors.danger }
      : tone === 'warn'
      ? { bg: colors.warningLight, border: colors.warningBorder, fg: colors.warning }
      : { bg: colors.successLight, border: colors.successBorder, fg: colors.success }
  );
  const clickable = typeof onClick === 'function';
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      style={{
        ...cardStyle,
        background: palette.bg,
        borderColor: palette.border,
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: colors.neutral600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 32,
          fontWeight: 700,
          color: palette.fg,
          fontVariantNumeric: 'tabular-nums',
          marginTop: 6,
          letterSpacing: '-0.02em',
        }}
      >
        {formatCount(value)}
      </div>
      {clickable && (
        <div style={{ fontSize: 12, color: colors.neutral600, marginTop: 4 }}>
          View list →
        </div>
      )}
    </div>
  );
}

function SchemeBar({
  scheme, count, total,
}: {
  scheme: string; count: number; total: number;
}): React.ReactElement {
  const pct = total ? (count / total) * 100 : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 13,
          marginBottom: 4,
          color: colors.neutral700,
        }}
      >
        <span style={{ fontWeight: 500 }}>{humaniseScheme(scheme)}</span>
        <span style={{ color: colors.neutral500, fontVariantNumeric: 'tabular-nums' }}>
          {formatCount(count)} ({percentage(count, total)})
        </span>
      </div>
      <div
        style={{
          background: colors.neutral100,
          height: 10,
          borderRadius: 5,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            background: colors.primary,
            width: `${pct}%`,
            height: '100%',
            transition: 'width 240ms ease',
          }}
        />
      </div>
    </div>
  );
}

// --- Styles -----------------------------------------------------------------

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 20,
  flexWrap: 'wrap',
  gap: 12,
};

const sectionStyle: React.CSSProperties = {
  marginBottom: 28,
};

const sectionHeading: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  marginBottom: 12,
  color: colors.neutral800,
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
};

const statusGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 10,
};

const cardStyle: React.CSSProperties = {
  padding: 16,
  border: `1px solid ${colors.neutral200}`,
  borderRadius: 10,
  background: colors.white,
};

const statusCardStyle: React.CSSProperties = {
  padding: 14,
  border: `1px solid ${colors.neutral200}`,
  borderRadius: 10,
  background: colors.white,
};

const schemeBarWrapStyle: React.CSSProperties = {
  maxWidth: 520,
  background: colors.white,
  border: `1px solid ${colors.neutral200}`,
  borderRadius: 10,
  padding: 16,
};
